import { useTranslation } from "react-i18next";
import { CommonDefectsMetrics } from "../../types/Metric";
import { useCallback, useMemo } from "react";
import { CommonDefectsGraph } from "../DefectInsights/CommonDefectsGraph";
import { Button } from "primereact/button";
import { mapDefectsMetrics } from "../../utils/DashboardHelper";
import { useNavigate } from "react-router-dom";
import { useAppDispatch } from "../../hooks/storeHooks";
import { DefectTabs } from "../../types/DefectTabs";
import { setActiveTab } from "../../store/slices/defectSlice";
import { sendGAEvent } from "../../AnalyticsService";
import { DASHBOARD_CATEGORY, DASHBOARD_COMMON_DEFECTS_DETAILED_VIEW_CLICK, DASHBOARD_COMMON_DEFECTS_BAR_CLICK } from "../../constants/analytics";
import { PlotMouseEvent } from "plotly.js";
import { defaultDefectsFiltersConfig } from "../DefectsTable/Filters/FilterTemplates";
import { DefectTableFields } from "../DefectsTable/DefectTableFields";
import { FilterMatchMode } from "primereact/api";
import { resetTableFilters } from "../../store/thunks/filtersThunks";
import LOCAL_TABLE_CONFIG_KEY from "../../types/TableLocalConfigKeys";

type Props = {
    metrics: CommonDefectsMetrics | undefined;
    loading: boolean;
};

export const CommonDefectsTiles = (props: Props) => {
    const { metrics, loading } = props;

    const { t } = useTranslation("locale");
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const data = useMemo(() => 
        mapDefectsMetrics(metrics?.common_defects)
    , [metrics]);

    const onClick = useCallback(() => {
        sendGAEvent(DASHBOARD_CATEGORY, DASHBOARD_COMMON_DEFECTS_DETAILED_VIEW_CLICK);
        navigate("/defects");
        dispatch(setActiveTab(DefectTabs.insights));
    }, []);

    const header = useMemo(
        () => (
            <div className="flex align-items-center">
                <h2>{t("dashboard.mostCommonDefects")}</h2>
                <div className="flex-grow-1" />
                <p>{t("dashboard.fromLatestInspections")}</p>
                <Button
                    label={t("dashboard.viewMore")}
                    text
                    onClick={onClick}
                />
            </div>
        ),
        []
    );

    const onBarClick = useCallback(
        (event: PlotMouseEvent) => {
            sendGAEvent(DASHBOARD_CATEGORY, DASHBOARD_COMMON_DEFECTS_BAR_CLICK);

            if (event.points.length) {
                const { y, data, pointIndex } = event.points[0];
                const typeCode = data.customdata[pointIndex];
                if (y && typeof y === "string") {
                    const tableFilters = {
                        ...defaultDefectsFiltersConfig,
                        [DefectTableFields.STATUS]: {
                            value: null,
                            matchMode: FilterMatchMode.IN
                        },
                        [DefectTableFields.TYPE_CODE]: {
                            value: [typeCode],
                            matchMode: FilterMatchMode.IN
                        }
                    };

                    dispatch(
                        resetTableFilters(
                            LOCAL_TABLE_CONFIG_KEY.DEFECTS,
                            tableFilters
                        )
                    );

                    navigate("/defects");
                    dispatch(setActiveTab(DefectTabs.defects));
                }
            }
        },
        [data]
    );

    if (!loading && !data?.length) {
        return (
            <div className="col-12">
                <div className="metric-card p-4 pt-3">
                    {header}
                    <div className="flex justify-content-center">
                        <p>{t("dashboard.noDefectsDataAvailable")}</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="col-12">
            <div className="metric-card p-4 pt-3">
                {header}
                <CommonDefectsGraph
                    className="defect-insight-graph-small"
                    data={data}
                    filterValues={[]}
                    loading={loading}
                    onClick={onBarClick}
                />
            </div>
        </div>
    );
};
